import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import CTAButton from '@components/CTAButton';
import TextButton from '@components/TextButton';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';
import { ROUTES } from '@shared/constants';
import CustomTextInput from '@components/CustomTextInput/index';
import { formHasErrors, getErrorMessageId, errorTypes } from '../validate';
import { withTranslation } from 'react-i18next';

import {
  SignInComponentContainer,
  SignInInputAreaContainer,
  SignInHeaderContainer,
  InputContainer,
  SignInButtonContainer,
  UnderButtontTextContainer,
  ScreenContainer,
  linkStyles,
  LinkContainer,
  StyledLogo,
  ServerErrorMessage,
} from './styles';
import LogoImage from '../logoTop.svg';

import { clearUiState, onSignIn } from './actions';
import reducer from './reducer';
import saga from './saga';
import ClientVersion from '@components/ClientVersion';
import ServerVersion from '@components/ServerVersion';
import { ENVIRONMENT } from '@services/servers';
import { isConnectionErrorSentinel } from '@utils/errorUtils';

class SignIn extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      visibility: {
        password: false,
      },
      form: {
        values: {
          email: '',
          password: '',
        },
        errors: {
          email: null,
          password: null,
        },
      },
    };
    this.throttledLogin = _.throttle(props.onSignInClick, 1000);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sending && !this.props.sending && this.state.loading) {
      // Request finished (success or failure) -> allow another attempt.
      // (On success this screen usually unmounts, but keep it safe.)
      this.setState({ loading: false });
    }
  }

  componentDidMount() {
    this.props.resetSignInViewState();
  }

  onFieldUpdate = (event) => {
    const { name, value } = event.target;

    this.setState((prevState) => {
      const newForm = prevState.form;
      newForm.values[name] = value;

      return {
        form: newForm,
      };
    });
  };

  onFocus = (event) => {
    const name = event.target.name;
    this.setState((prevState) => {
      const newForm = prevState.form;
      newForm.errors[name] = null;

      return {
        form: newForm,
      };
    });
  };

  onLoginButtonClick = () => {
    if (this.props.sending || this.state.loading) {
      return;
    }

    const { values } = this.state.form;

    const formErrors = this.getFormErrors();
    const newForm = _.cloneDeep(this.state.form);

    const loading = !formHasErrors(formErrors);
    newForm.errors = formErrors;

    this.setState({
      form: newForm,
      loading,
    });

    if (loading) {
      this.throttledLogin(values.email, values.password);
    }
  };

  getFieldError = (messageId) => {
    if (
      !messageId ||
      messageId === errorTypes.simplePassword ||
      messageId === errorTypes.weakPassword
    ) {
      return null;
    }
    return this.props.t(`Errors.${messageId}`)
  };

  getFormErrors = () => {
    const { values } = this.state.form;
    const newErrors = {};

    _.forEach(values, (fieldValue, fieldName) => {
      const messageId = getErrorMessageId(fieldName, fieldValue);
      newErrors[fieldName] = this.getFieldError(messageId);
    });

    return newErrors;
  };

  renderHeader = () => (
    <SignInHeaderContainer>
      <StyledLogo src={LogoImage} alt="logo" />
      <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H1}>
        {this.props.t('Actions.Auth.signIn')}
      </Text>
    </SignInHeaderContainer>
  );

  renderInputs = () => {
    const { email, password } = this.state.form.values;

    return (
      <div>
        <InputContainer>
          <CustomTextInput
            onEnter={this.onLoginButtonClick}
            type="email"
            id="signin_email"
            label={this.props.t('Authentication.Email.email')}
            name="email"
            value={email}
            error={this.state.form.errors.email}
            onChange={this.onFieldUpdate}
            onFocus={this.onFocus}
          />
        </InputContainer>
        <InputContainer>
          <CustomTextInput
            onEnter={this.onLoginButtonClick}
            type="password"
            id="signin_password"
            label={this.props.t('Authentication.Password.password')}
            name="password"
            value={password}
            error={this.state.form.errors.password}
            onChange={this.onFieldUpdate}
            onFocus={this.onFocus}
            showPasswordToggle
          />
          <LinkContainer>
            <Link
              id="signin_forgot_password"
              style={linkStyles}
              to={ROUTES.FORGOT_PASSWORD}
            >
              <TextButton>
                <Text size={TEXT_SIZE.H4}>
                  {this.props.t('Authentication.Password.forgot')}
                </Text>
              </TextButton>
            </Link>
          </LinkContainer>
        </InputContainer>
      </div>
    );
  };

  renderButton = () => {
    const inProgress = this.props.sending || this.state.loading;
    const rawServerError = String(this.props.serverError || '').trim();
    const normalizedServerErrorKey = (() => {
      if (!rawServerError) return null;
      if (isConnectionErrorSentinel({ message: rawServerError, code: rawServerError })) {
        return 'connectionErrorCheckInternet';
      }
      if (rawServerError.startsWith('Errors.')) {
        return rawServerError.slice('Errors.'.length);
      }
      return rawServerError;
    })();

    const serverErrorText = normalizedServerErrorKey
      ? this.props.t(`Errors.${normalizedServerErrorKey}`)
      : null;

    const serverError = serverErrorText ? (
      <ServerErrorMessage type="CUSTOM" customText={serverErrorText} />
    ) : null;

    return (
      <SignInButtonContainer>
        <CTAButton
          id="signin_button"
          onClick={this.onLoginButtonClick}
          expanded
          disabled={inProgress}
        >
          {inProgress
            ? this.props.t('Actions.Auth.inProgress')
            : this.props.t('Actions.Auth.signIn')}
        </CTAButton>
        {serverError}
      </SignInButtonContainer>
    );
  };

  renderUnderButtonContainer = () => (
    <UnderButtontTextContainer>
      <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4}>
        {this.props.t('Authentication.Distribution.newToTheApp') + ' '}
      </Text>
      <Link
        id="newtonovotalk_signup_button"
        style={linkStyles}
        to={ROUTES.SIGN_UP}
      >
        <TextButton>
          <Text size={TEXT_SIZE.H4}>
            {this.props.t('Authentication.Distribution.toSignUp')}
          </Text>
        </TextButton>
      </Link>
      {ENVIRONMENT !== 'prod' && (
        <>
          <ClientVersion />
          <ServerVersion />
        </>
      )}
    </UnderButtontTextContainer>
  );

  render() {
    return (
      <ScreenContainer>
        <SignInComponentContainer className="signIn">
          {this.renderHeader()}
          <SignInInputAreaContainer>
            {this.renderInputs()}

            {this.renderButton()}

            {this.renderUnderButtonContainer()}
          </SignInInputAreaContainer>
        </SignInComponentContainer>
      </ScreenContainer>
    );
  }
}

SignIn.defaultProps = {
  onSignInClick: () => {},
};

SignIn.propTypes = {
  onSignInClick: PropTypes.func.isRequired,
  resetSignInViewState: PropTypes.func.isRequired,
  serverError: PropTypes.string,
  sending: PropTypes.bool,
};

export const mapStateToProps = (state) => {
  const serverError = state.getIn(['signIn', 'errors', 'serverError']);
  const sending = state.getIn(['signIn', 'sending']);
  return { serverError, sending };
};

export const mapDispatchToProps = (dispatch) => ({
  onSignInClick: (email, password) => {
    dispatch(onSignIn(email, password));
  },
  resetSignInViewState: () => {
    dispatch(clearUiState());
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withSaga = injectSaga({ key: 'signIn', saga });
const withReducer = injectReducer({ key: 'signIn', reducer });

export default compose(
  withConnect,
  withReducer,
  withSaga,
  withTranslation(),
)(SignIn);
